import React from "react";
import { Bell, CheckCheck, AlertTriangle, CheckCircle2, Info, History } from "lucide-react";
import { api } from "@/lib/api";
import { useList } from "@/hooks/useApi";
import { useQueryClient } from "@tanstack/react-query";
import { PageHeader, Loading, EmptyState } from "@/components/common";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";
import { cn } from "@/lib/utils";

const ICONS = { danger: AlertTriangle, warning: AlertTriangle, success: CheckCircle2, info: Info };
const TONE = { danger: "text-destructive bg-destructive/12", warning: "text-warning bg-warning/12", success: "text-success bg-success/12", info: "text-primary bg-primary/12" };

export default function Notifications() {
  const qc = useQueryClient();
  const { data: notifs, isLoading } = useList("notifications", "/notifications");
  const { data: activity } = useList("activity", "/activity");

  const markAll = async () => { await api.post("/notifications/read-all"); qc.invalidateQueries(); };
  const markOne = async (id) => { await api.post(`/notifications/${id}/read`); qc.invalidateQueries(); };

  if (isLoading) return <Loading />;
  return (
    <div>
      <PageHeader title="Notifications & Activity" subtitle="Alerts and full audit trail" testid="notifications-header">
        <Button variant="outline" onClick={markAll} className="gap-2" data-testid="mark-all-read-btn"><CheckCheck className="w-4 h-4" /> Mark all read</Button>
      </PageHeader>
      <Tabs defaultValue="alerts">
        <TabsList className="mb-4">
          <TabsTrigger value="alerts" data-testid="tab-alerts"><Bell className="w-4 h-4 mr-1.5" /> Alerts</TabsTrigger>
          <TabsTrigger value="activity" data-testid="tab-activity"><History className="w-4 h-4 mr-1.5" /> Activity Log</TabsTrigger>
        </TabsList>

        <TabsContent value="alerts">
          {!notifs?.length ? <EmptyState title="No notifications" /> : (
            <div className="space-y-2">
              {notifs.map((n) => {
                const Icon = ICONS[n.level] || Info;
                return (
                  <Card key={n.id} className={cn("p-4 flex items-center gap-3", !n.read && "border-l-4 border-l-primary")} data-testid={`notif-${n.id}`}>
                    <div className={cn("w-9 h-9 rounded-lg flex items-center justify-center shrink-0", TONE[n.level] || TONE.info)}><Icon className="w-5 h-5" /></div>
                    <div className="flex-1 min-w-0">
                      <p className={cn("text-sm", !n.read && "font-semibold")}>{n.message}</p>
                      <p className="text-xs text-muted-foreground">{new Date(n.created_at).toLocaleString()}</p>
                    </div>
                    {!n.read && <Button size="sm" variant="ghost" onClick={() => markOne(n.id)}>Read</Button>}
                  </Card>
                );
              })}
            </div>
          )}
        </TabsContent>

        <TabsContent value="activity">
          {!activity?.length ? <EmptyState title="No activity yet" /> : (
            <div className="space-y-1.5">
              {activity.map((a) => (
                <Card key={a.id} className="p-3 flex items-center gap-3 text-sm" data-testid={`activity-${a.id}`}>
                  <div className="w-7 h-7 rounded-full bg-secondary flex items-center justify-center text-xs font-bold shrink-0">{(a.user_name || "?")[0]}</div>
                  <div className="flex-1 min-w-0">
                    <span className="font-medium">{a.user_name}</span>
                    <span className="text-muted-foreground"> · {a.action?.replace(/_/g, " ")} · </span>
                    <span>{a.entity} {a.record}</span>
                    {a.previous_value != null && <span className="text-muted-foreground"> ({a.previous_value} → {a.new_value})</span>}
                  </div>
                  <span className="text-xs text-muted-foreground whitespace-nowrap">{new Date(a.created_at).toLocaleString()}</span>
                </Card>
              ))}
            </div>
          )}
        </TabsContent>
      </Tabs>
    </div>
  );
}
